import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark, MapPin, Eye, Play } from 'lucide-react';
import StarRating from '@/components/StarRating';
import Avatar from '@/components/Avatar';
import useLongPress from '@/hooks/useLongPress';
import CardPeekSheet from '@/components/CardPeekSheet';

export default function ProduceCard({ post, isSaved, onToggleSave, index = 0 }) {
  const navigate = useNavigate();
  const [peekOpen, setPeekOpen] = useState(false);

  const bind = useLongPress({
    onLongPress: () => setPeekOpen(true),
    onClick: () => navigate(`/produce/${post.id}`),
  });

  const handleSave = (e) => {
    e.stopPropagation();
    onToggleSave?.(post.id);
  };

  return (
    <>
    <div
      data-testid={`produce-card-${post.id}`}
      {...bind}
      style={{ animationDelay: `${index * 0.05}s`, opacity: 0, touchAction: 'pan-y', WebkitUserSelect: 'none', userSelect: 'none' }}
      className="group bg-[#F6F8FB] rounded-3xl border border-[#E4E8EF] overflow-hidden hover:border-[#0B4F3F]/25 hover:shadow-[0_20px_60px_-25px_rgba(11,79,63,0.15)] active:scale-[0.99] transition-all duration-300 cursor-pointer animate-fade-in-up select-none"
    >
      {/* Image */}
      <div className="relative w-full h-52 md:h-60 bg-[#EFF2F7] overflow-hidden">
        {post.images?.[0] ? (
          <img
            src={post.images[0]}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#5B6675]/40">
            <span className="text-5xl">🌾</span>
          </div>
        )}
        {/* Subtle bottom scrim for premium depth */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0B1620]/40 to-transparent pointer-events-none" />
        {/* Category chip */}
        <span className="absolute top-3 left-3 px-2.5 py-1 bg-[#F6F8FB]/70 backdrop-blur-md text-[#0B1620] text-[10px] font-semibold tracking-wide uppercase rounded-full border border-[#E4E8EF]">
          {post.category}
        </span>
        {/* Save button */}
        <button
          data-testid={`save-btn-${post.id}`}
          onClick={handleSave}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          aria-label={isSaved ? 'Remove from saved' : 'Save'}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#F6F8FB]/70 backdrop-blur-md flex items-center justify-center border border-[#E4E8EF] hover:border-[#0B4F3F]/40 active:scale-90 transition-all"
        >
          <Bookmark
            className={`w-4 h-4 ${isSaved ? 'fill-[#0B4F3F] text-[#0B4F3F]' : 'text-[#0B1620]'}`}
          />
        </button>
        {/* Video count chip */}
        {post.videos && post.videos.length > 0 && (
          <span
            data-testid={`video-chip-${post.id}`}
            className="absolute top-3 right-14 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#0B1620]/70 backdrop-blur-md text-[#F6F8FB] text-[10px] font-semibold"
          >
            <Play className="w-2.5 h-2.5" fill="currentColor" />
            {post.videos.length} video{post.videos.length > 1 ? 's' : ''}
          </span>
        )}
        {/* Price overlay */}
        <div className="absolute bottom-3 left-3 flex items-baseline gap-0.5 px-3 py-1.5 rounded-full bg-[#0B4F3F] text-[#F6F8FB] shadow-[0_6px_20px_-6px_rgba(11,79,63,0.5)]">
          <span className="text-sm font-bold font-['Bricolage_Grotesque']">₹{post.price_per_unit}</span>
          <span className="text-[10px] font-semibold opacity-80">/{post.unit}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-base font-semibold text-[#0B1620] line-clamp-1 font-['Bricolage_Grotesque'] tracking-tight">
            {post.title}
          </h3>
          <span className="text-[11px] font-semibold text-[#0B4F3F] whitespace-nowrap tabular-nums">
            {post.quantity} {post.unit}
          </span>
        </div>

        <p className="text-[13px] text-[#5B6675] line-clamp-2 leading-relaxed">{post.description}</p>

        <div className="flex items-center justify-between pt-2 border-t border-[#E4E8EF]/60">
          {/* Farmer info */}
          <div className="flex items-center gap-2.5">
            <Avatar name={post.farmer_name} src={post.farmer_avatar} size={32} className="ring-1 ring-[#E4E8EF]" />
            <div className="flex flex-col leading-tight">
              <span className="text-[12px] font-medium text-[#0B1620]">{post.farmer_name}</span>
              <div
                className="flex items-center gap-1 mt-0.5"
                data-testid={`card-rating-${post.id}`}
              >
                <StarRating value={post.farmer_avg_rating || 0} size={11} />
                {post.farmer_review_count > 0 ? (
                  <span className="text-[10px] text-[#5B6675]">
                    <span className="font-semibold text-[#0B1620]">{post.farmer_avg_rating?.toFixed(1)}</span>
                    <span className="ml-0.5">({post.farmer_review_count})</span>
                  </span>
                ) : (
                  <span className="text-[10px] text-[#5B6675]/70">New</span>
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-2 text-[#5B6675]">
            {post.location && (
              <span className="text-[10px] flex items-center gap-0.5 max-w-[80px] truncate">
                <MapPin className="w-3 h-3" />
                <span className="truncate">{post.location}</span>
              </span>
            )}
            <span className="flex items-center gap-1 text-[10px] tabular-nums">
              <Eye className="w-3 h-3" />
              {post.views || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
    <CardPeekSheet open={peekOpen} onOpenChange={setPeekOpen} kind="produce" post={post} />
    </>
  );
}
